import { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Avatar,
  Chip,
  Button,
  Stack,
  Sheet
} from '@mui/joy';
import { Visibility, DragIndicator } from '@mui/icons-material';
import { APPLICATION_STAGES, APPLICATION_STAGES_LABELS, APPLICATION_STAGES_COLORS } from '../constants';
import { formatTimestamp, getCdnFileUrl } from '../utils';

const getScoreColor = (score) => {
  if (score >= 80) return 'success';
  if (score >= 60) return 'warning';
  return 'danger';
};

const KanbanColumn = ({ stage, applications, onViewDetails, onMoveCandidate }) => {
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const candidateData = JSON.parse(e.dataTransfer.getData('text/plain'));
    if (candidateData.currentStage !== stage) {
      onMoveCandidate(candidateData.applicationId, stage);
    }
  };

  return (
    <Sheet
      variant="outlined"
      sx={{
        minHeight: 500,
        flex: '1 1 0',
        minWidth: { xs: 250, sm: 280, md: 320 },
        maxWidth: { xs: 280, sm: 350, md: 400 },
        p: { xs: 2, sm: 3 },
        borderRadius: 'md',
        backgroundColor: 'background.level1',
        border: '2px solid',
        borderColor: 'divider',
        '&:hover': {
          borderColor: 'primary.300'
        }
      }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <Typography level="h4" mb={3} textAlign="center" fontWeight="lg">
        {APPLICATION_STAGES_LABELS[stage]}
        <Chip
          size="sm"
          color={APPLICATION_STAGES_COLORS[stage]}
          variant="soft"
          sx={{ ml: 2 }}
        >
          {applications.length}
        </Chip>
      </Typography>
      
      <Stack spacing={3}>
        {applications.map((application) => (
          <CandidateCard
            key={application._id}
            application={application}
            currentStage={stage}
            onViewDetails={onViewDetails}
          />
        ))}
        
        {applications.length === 0 && (
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              color: 'text.tertiary',
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 'md',
              backgroundColor: 'background.surface'
            }}
          >
            <Typography level="body-sm">
              Drop candidates here
            </Typography>
          </Box>
        )}
      </Stack>
    </Sheet>
  );
};

const CandidateCard = ({ application, currentStage, onViewDetails }) => {
  const [isDragging, setIsDragging] = useState(false);
  const isRejected = currentStage === APPLICATION_STAGES.REJECTED;

  const handleDragStart = (e) => {
    if (isRejected) {
      e.preventDefault();
      return;
    }
    setIsDragging(true);
    const candidateData = {
      applicationId: application._id,
      currentStage: currentStage
    };
    e.dataTransfer.setData('text/plain', JSON.stringify(candidateData));
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        cursor: isRejected ? 'default' : 'grab',
        opacity: isDragging ? 0.5 : (isRejected ? 0.8 : 1),
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: isRejected ? 'none' : 'md',
          borderColor: isRejected ? 'divider' : 'primary.500'
        },
        '&:active': { cursor: isRejected ? 'default' : 'grabbing' },
        p: 3
      }}
      draggable={!isRejected}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <DragIndicator sx={{ fontSize: 18, color: 'text.tertiary' }} />
        {application.user.avatarUrl ? (
          <Avatar
            size="md"
            src={getCdnFileUrl(application.user.avatarUrl)}
          />
        ) : (
          <Avatar size="md">
            {application.user.firstName[0]}{application.user.lastName[0]}
          </Avatar>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography level="title-md" sx={{ fontWeight: 'lg' }}>
            {application.user.firstName} {application.user.lastName}
          </Typography>
          <Typography level="body-sm" color="text.tertiary">
            {formatTimestamp(application.updatedAt, 'll')}
          </Typography>
        </Box>
      </Box>

      {application.evaluation && (
        <Box sx={{ mb: 2 }}>
          <Chip
            size="md"
            color={getScoreColor(application.finalScore)}
            variant="soft"
            sx={{ fontWeight: 'md' }}
          >
            Score: {application.finalScore}/100
          </Chip>
        </Box>
      )}

      <Button
        variant="outlined"
        color="primary"
        size="sm"
        onClick={() => onViewDetails(application)}
        startDecorator={<Visibility />}
        fullWidth
        sx={{ 
          fontWeight: 'md',
          '&:hover': {
            backgroundColor: 'primary.50'
          }
        }}
      >
        View Details
      </Button>
    </Card>
  );
};

const KanbanBoard = ({ applications, onViewDetails, onMoveCandidate }) => {
  // Group applications by stage, defaulting to APPLICATION_RECEIVED if no stage is set
  const applicationsByStage = Object.values(APPLICATION_STAGES).reduce((acc, stage) => {
    acc[stage] = applications.filter(app => (app.stage || APPLICATION_STAGES.APPLICATION_RECEIVED) === stage);
    return acc;
  }, {});

  return (
    <Box sx={{ 
      width: '100%',
      backgroundColor: 'background.body',
      borderRadius: 'lg',
      p: { xs: 2, sm: 3 }
    }}>
      <Box sx={{ 
        display: 'flex', 
        gap: { xs: 2, sm: 3 }, 
        overflowX: 'auto',
        pb: 2,
        minHeight: 600,
        '&::-webkit-scrollbar': {
          height: 8,
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'background.level1',
          borderRadius: 'sm',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'neutral.400',
          borderRadius: 'sm',
          '&:hover': {
            backgroundColor: 'neutral.500',
          },
        },
      }}>
        {Object.values(APPLICATION_STAGES).map((stage) => (
          <KanbanColumn
            key={stage}
            stage={stage}
            applications={applicationsByStage[stage]}
            onViewDetails={onViewDetails}
            onMoveCandidate={onMoveCandidate}
          />
        ))}
      </Box>
    </Box>
  );
};

export default KanbanBoard;